import { useTranslations } from "next-intl";
import { UploadItem } from "../../../upload-store";
import styles from "./upload-dialog.module.css";
import { IoDocumentText } from "react-icons/io5";

type Props = {
  uploads: UploadItem[];
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  caption: string;
  setCaption: (v: string) => void;
  onRemove: (id: string) => void;
  onCancel: () => void;
  onSend: () => void;
};

export default function UploadDialog({
  uploads,
  activeIndex,
  setActiveIndex,
  caption,
  setCaption,
  onRemove,
  onCancel,
  onSend,
}: Props) {
  const t = useTranslations("ChatsView");
  if (!uploads.length) return null;
  const safeIndex = Math.min(activeIndex, uploads.length - 1);
  const upload = uploads[safeIndex];
  const type = getFileType(upload.file);

  // 🧠 Send button enabled only if at least one uploaded
  const canSend =
    uploads.length > 0 && uploads.every((u) => u.Status === "UPLOADED");
  const hasError = uploads.some((u) => u.Status === "ERROR");

  return (
    <div className={styles.wrapper}>
      <div className={styles.dialog}>
        {/* MAIN PREVIEW */}
        <div className={styles.preview}>
          {type === "image" && (
            <img src={upload.PreviewUrl} alt={upload.file.name} />
          )}

          {type === "video" && (
            <video src={upload.PreviewUrl} controls className={styles.video} />
          )}

          {type === "pdf" && (
            <div className={styles.fileCard}>
              <div className={styles.fileIcon}>
                <IoDocumentText />
              </div>
              <div className={styles.fileMeta}>
                <div className={styles.fileName}>{upload.file.name}</div>
                <div className={styles.fileSize}>
                  {formatFileSize(upload.file.size)}
                </div>
              </div>
            </div>
          )}

          {type === "document" && (
            <div className={styles.fileCard}>
              <div className={styles.fileIcon}>
                <IoDocumentText />
              </div>
              <div className={styles.fileMeta}>
                <div className={styles.fileName}>{upload.file.name}</div>
                <div className={styles.fileSize}>
                  {formatFileSize(upload.file.size)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* THUMB STRIP */}
        <div className={styles.thumbs}>
          {uploads.map((u, i) => {
            const type = getFileType(u.file);

            return (
              <div
                key={u.id} // ✅ FIXED
                className={`${styles.thumb} 
                ${i === safeIndex ? styles.active : ""} 
                ${u.Status === "ERROR" ? styles.thumbError : ""}`}
                onClick={() => setActiveIndex(i)}
              >
                {/* Thumbnail */}
                {type === "image" && (
                  <img src={u.PreviewUrl} alt={u.file.name} />
                )}

                {type === "video" && (
                  <div className={styles.videoThumb}>
                    <video src={u.PreviewUrl} />
                    <div className={styles.playIcon}>▶</div>
                  </div>
                )}

                {type !== "image" && type !== "video" && (
                  <div className={styles.fileThumb}>
                    <span className={styles.fileExt}>
                      {getDisplayExtension(u.file.name)}
                    </span>
                  </div>
                )}
                {/* Overlay status */}
                {u.Status !== "UPLOADED" && (
                  <div className={styles.overlay}>
                    {u.Status === "PRESIGNING" && (
                      <div className={styles.spinner} />
                    )}

                    {u.Status === "UPLOADING" && (
                      <span>{Math.round(u.progress)}%</span>
                    )}

                    {u.Status === "ERROR" && (
                      <div className={styles.errorOverlay}>
                        <span className={styles.errorText}>Failed</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Remove button */}
                <button
                  className={styles.remove}
                  disabled={u.Status === "UPLOADING"}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(u.id);
                  }}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>

        {/* FOOTER */}
        <div className={styles.footer}>
          <input
            className={styles.caption}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Scrie un mesaj..."
          />

          <button onClick={onCancel} className={styles.cancel}>
            ✕
          </button>

          <button
            onClick={onSend}
            className={styles.send}
            disabled={!canSend || hasError}
          >
            {t("send")}
          </button>
        </div>
      </div>
    </div>
  );
}

const DOC_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

export function getFileType(file: File) {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";

  if (file.type === "application/pdf") return "pdf";
  if (DOC_TYPES.has(file.type)) return "document";

  return "document";
}

export function getDisplayExtension(name: string) {
  return getExtension(name).toUpperCase();
}

export function getExtension(name: string) {
  const ext = name.split(".").pop();
  return ext ? ext.toLowerCase() : "";
}

export function formatFileSize(bytes: number) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  if (bytes < 1024 * 1024 * 1024)
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";

  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + " GB";
}
